import { useState, useEffect, useRef, useContext } from 'react';
import RemoveIcon from '@mui/icons-material/Remove';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import classes from './Chatbot.module.css';
import AuthContext from '../../../contexts/auth-context';
import AppContext from '../../../contexts/app-context';
import { chatbotCase, urls, toastify_status } from '../../config/variables';
import ReactMarkdown from 'react-markdown'; // ---> used to given a string of markdown
import remarkGfm from 'remark-gfm'
import { validateOpenAiSessionId, validateOpenAiAssistantResponse } from '../../../Utils/validateHttpResponse';
import TypingDots from '../TypingDots/TypingDots';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import TextField from '@mui/material/TextField';
import useAxios from '../../../hooks/useAxios';
import Draggable from 'react-draggable';
import Toastify from '../Toastify';
import * as motion from "motion/react-client"
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageIcon from '@mui/icons-material/Image';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import StopIcon from '@mui/icons-material/Stop';
import { Mic, MicOff } from '@mui/icons-material'
import CloudLoadingAnimation from './CloudLoadingAnimation';
import { framerMotionAnimation, fontSizes, transitions} from '../../../constants/GlobalStyles';

const Chatbot = () => {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const token = authCtx.token;
  const userRole = appCtx.userRole
  const gender = authCtx.userProfile.user.gender;
  const { operation } = useAxios();
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]); // ---> this is the chat history that is displayed to the user.
  const [userInput, setUserInput] = useState({ role: 'user', content: '', imageFiles: [], imagePreviews: [] });
  const [sessionId, setSessionId] = useState(''); // ---> openai api key
  const [assistantAnswer, setAssistantAnswer] = useState('');
  const [nlpMod, setNlopMod] = useState(false);
  const [resize, setResize] = useState(false)
  const lastMessageRef = useRef(null);
  const draggableNodeRef = useRef(null);
  const tailRef = useRef(null);
  const lastMessageRole = chatHistory[chatHistory?.length - 1] && chatHistory[chatHistory?.length - 1].role
  const [hideScrollIcon, setHideScrollIcon] = useState(false);
  // settings menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const openSettingsMenu = Boolean(anchorEl);
  const imageInputRef = useRef()
  // dialog image
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [dialogImage, setDialogImage] = useState('');
  const [position, setPosition] = useState({x: 0, y: 0}) // ---> chatbot position

  // web socket states
  const [isRecording, setIsRecording] = useState(false); // ---> user recording
  const [isSpeaking, setIsSpeaking] = useState(false); // ---> user recording
  const [isListening, setIsListening] = useState(false) // ---> user listening
  const [loadingVoiceResponse, setLoadingVoiceResponse] = useState(false); // ---> processing audio response
  const [play, setPlay] = useState(false) // ---> play or stop chunks response
  const [isSpeakingFromPlayback, setIsSpeakingFromPlayback] = useState(false); // ---> catch the audio chunk is speaking or silent
  const [wsReady, setWsReady] = useState(false);
  const socketRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const [chunksResponse, setChunksResponse] = useState([])
  const [count, setCount] = useState(0)
  const webSocketBaseUrl = urls.BASE_URL.replace(/^https?:/, '');
 const currentSourceRef = useRef(null);
  const [voiceMode, setVoiceMode] = useState(false)


  // get session_id from ml_server
  useEffect(() => {
    // only doctor and patient can use the chatbot
    if(userRole === 'doctor' || userRole === 'patient') {
      getChatbotSessionId()
    }
    // eslint-disable-next-line
  }, [])

  const getChatbotSessionId = () => {
    operation(
          {
            method: 'POST',
            url: urls.SESSION_ID,
            headers: { 'Content-Type': 'application/json', Authorization: `token ${token}` },
            data: {},
          },
          handleSetSessionId,
          handleSessionIdError
        );
  }

  const handleSetSessionId = (response) => {
    if(validateOpenAiSessionId(response)) {
      setSessionId(response)
    } else {
      Toastify({message: 'invalid Open Ai session id', state: toastify_status.ERROR})
    }
  }

  const handleSessionIdError = () => {
    Toastify({message: 'Something went wrong while fetching the session ID. Please try again later.', state: toastify_status.ERROR})
  }

  const sendMessage = async () => {
    if(userInput.content) {
      setChatHistory((preV) => [...preV, userInput ])
      setUserInput({ role: 'user', content: '', imageFiles: [], imagePreviews: [] })
      setLoading(true)

      const formData = new FormData()
      if(userInput.imageFiles.length > 0) {
        userInput.imageFiles.map((file) => formData.append('image', file))
      }
      formData.append('message', userInput.content)
      formData.append('mode', nlpMod ? 'nlp' : '')

      operation(
        {
          method: 'POST',
          url: urls.MESSAGE.replace(':sessionId', sessionId.session_id),
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `token ${token}` },
          data: formData,
        },
        handleSetAssistantResponse,
        handleAssistantResponseError
      );
    }
  }

  const handleSetAssistantResponse = (answer) => {
    if (validateOpenAiAssistantResponse(answer)) {
      setAssistantAnswer(answer.response)
      setIsStreaming(true)
      setLoading(false)
      setChatHistory(prev => [...prev, { role: 'assistant', content: '' }]); // ---> initial an empty assistant message to stream the ai answer
    } else {
      Toastify({ message: 'invalid Open Ai Assistant response data', state: toastify_status.ERROR })
      setLoading(false)
    }
  }

  const handleAssistantResponseError = () => {
    Toastify({message: 'Something went wrong while fetching assistant response, please try again later.', state: toastify_status.ERROR})
    setLoading(false)
  }

  // streaming assistant response, it will modify the last message inside chat history and tops when all characters are appended.
  useEffect(() => {
    if (assistantAnswer && isStreaming) {
      let currentContent = '';
      let index = 0;
      const interval = setInterval(() => {
        if (!isStreaming) {
          clearInterval(interval);
          return;
        }

        if (index >= assistantAnswer.length) {
          clearInterval(interval);
          setIsStreaming(false);
          return;
        }

        currentContent += assistantAnswer[index];
        index++;

        setChatHistory(prev => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          if (updated[lastIndex].role === 'assistant') {
            updated[lastIndex] = { ...updated[lastIndex], content: currentContent };
          }
          return updated;
        });
      }, 10);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line
  }, [assistantAnswer, isStreaming]);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleChange = (e) => {
    if (!loading && !isStreaming) {
      setUserInput({ ...userInput, content: e.target.value });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  // clear chat history and starat a new session
  const clearChatHistory = () => {
    getChatbotSessionId()
    setChatHistory([])
    setIsStreaming(false)
    setLoading(false)
    if(isRecording) {
      stopRecording()
    }
  }

  // scroll down when asking a new question
  useEffect(() => {
    if (lastMessageRef.current && lastMessageRole === 'user') {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // eslint-disable-next-line
  }, [chatHistory]);

  // stop openai answer streaming
  const stopStreaming = () => {
    if(!loading) {
      setIsStreaming(false)
    }
  }

  const handleClickSettingsMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseSettingsMenu = () => {
    setAnchorEl(null);
  };

  const settingsConfig = [
    {
      icon: <ClearAllIcon className={classes.menu_icon} />,
      text: 'New Chat',
      newSessionId: true
    },
  ]

  const SettingComponent = () => {
    return settingsConfig.map((config, i) => {
      return (
        <div
          key={i}
          className={classes.setting_component}
          onClick={() => handleClickSetting(config)}
        >
          {config.icon}
          <p className={classes.settingComponentText}>
            {config.text}
          </p>
        </div>
      );
    });
  };

  const handleClickSetting = (config) => {
    if (config.newSessionId) {
      clearChatHistory()
      setNlopMod(false)
    } else {
      return null
    }
    setAnchorEl(null)
  }

  const handleChangeMod = () => {
    setNlopMod((preV) => !preV);
    setVoiceMode(false)
    setUserInput((preV) => {
      return {
        ...preV,
        imageFiles: [],
        imagePreviews: []
      }
    })
  };

  const handleScroll = () => {
    tailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // show or hide scroll down icon
  useEffect(() => {
    if(isOpen) {
      const target = tailRef.current;
    
      if (!target) return;
    
      const observer = new IntersectionObserver(
        ([entry]) => {
          setHideScrollIcon(entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0.5,
        }
      );
    
      observer.observe(target);
    
      return () => {
        observer.unobserve(target);
      };
    }
    // eslint-disable-next-line
  }, [lastMessageRole, isOpen, voiceMode]);

  const handleReSize = () => {
    setResize((preV) => !preV)
  }

  const handleSelectImages = (e) => {
    const files =  [...e.target.files]
    const previewUrls = files.map((file) => (
      URL.createObjectURL(file)
    ))
    setUserInput({...userInput, imageFiles: files, imagePreviews: previewUrls })
  }

  const handleDeleteImage = (imageIndex) => {
    setUserInput((preV) => {
      return {
        ...preV,
        imageFiles: preV.imageFiles.filter((image, index) => index !== imageIndex),
        imagePreviews: preV.imagePreviews.filter((image, index) => index !== imageIndex)
      }
    })
  }

  const handleClickOpenImageDialog = (image) => {
    setOpenImageDialog(true);
    setDialogImage(image)
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setDialogImage('')
  };

  const handleDrag = (e, data) => {
    setPosition({x: data.x, y: data.y})
  }

  // reset position when window is resized
  useEffect(() => {
    const handleResize = () => {
      setPosition({x: 0, y: 0})
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line
  }, [])

  // web socket event handler
  useEffect(() => {
    if (sessionId.session_id) {
      const ws = new WebSocket(urls.webSocket.replace(':baseUrl', webSocketBaseUrl).replace(':sessionIdQuery', `?session_id=${sessionId.session_id}`));
      socketRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'connection_established') {
          console.log('Server:', data);
          setWsReady(true);
        } else if (data.type === 'lifecycle_event' && data.event === 'turn_started') {
          setChunksResponse([])
          setCount(0)
          setPlay(true)
          setChatHistory(prev => [...prev, { role: 'user', content: 'voice chat' }, { role: 'assistant', content: 'voice chat' }]);
        } else if (data.type === 'processing_started') {
          setLoadingVoiceResponse(true)
        } else {
          console.log('Message from server:', data);
          if(data.type === 'audio_response') {
            setChunksResponse((preV) => [...preV, data.audio])
            setIsListening(true)
            setLoadingVoiceResponse(false)
          }
        }
      };

      ws.onerror = (err) => console.error('WebSocket error:', err);
      ws.onclose = () => console.log('WebSocket closed');

      return () => ws.close();
    }
    // eslint-disable-next-line
  }, [sessionId]);

  // play chunk by chunk depend on the count
  useEffect(() => {
    if(isListening && play) {
      playChunk(chunksResponse[count])
    }
    // eslint-disable-next-line
  }, [isListening, count, play])

  // play a specific chunk response
  const playChunk = (base64Audio) => {
    if (base64Audio) {
      console.log('play the chunk ' + (count + 1) + ' of ' + chunksResponse.length + ' chunk')
      const binary = atob(base64Audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      // Convert to Float32 PCM
      const sampleRate = 24000;
      const numSamples = bytes.length / 2;
      const floatBuffer = new Float32Array(numSamples);
      for (let i = 0; i < numSamples; i++) {
        const lo = bytes[i * 2];
        const hi = bytes[i * 2 + 1];
        let sample = (hi << 8) | lo;
        if (sample >= 0x8000) sample -= 0x10000; // signed
        floatBuffer[i] = sample / 32768; // normalize
      }

      const audioCtx = new AudioContext();
      const buffer = audioCtx.createBuffer(1, floatBuffer.length, sampleRate);
      const hasSpeech = floatBuffer.some(sample => Math.abs(sample) > 0.02);
      setIsSpeakingFromPlayback(hasSpeech); // ---> catch the audio chunk is speaking or silent
      buffer.copyToChannel(floatBuffer, 0);

      const source = audioCtx.createBufferSource();
      currentSourceRef.current = source;
      source.buffer = buffer;
      source.connect(audioCtx.destination);

      source.onended = () => {
        console.log("Audio playback finished");
        setIsSpeakingFromPlayback(false);
        setCount(count + 1)
      };

      source.start();
    } else {
      setIsListening(false)
      setCount(0)
      setChunksResponse([])
    }
  };

 // stop playing the current chenk and prevent the next chunk to play and empty the response chunks
 const stopPlayback = () => {
  if (currentSourceRef.current) {
    try {
      currentSourceRef.current.stop(); // Stop current audio
    } catch (e) {
      console.warn("Already stopped or error:", e);
    }
    currentSourceRef.current = null;
  }
  setIsListening(false)
  setIsSpeaking(false)
  setChunksResponse([]);
  setPlay(false)
  setLoadingVoiceResponse(false)

  // set count to 0 after 1 second because when the current chunk get stopped it will finish and increase the count by 1
  setTimeout(() => {
    setCount(0)
  }, 1000)
};

  // start recording the user audio
  const startRecording = async () => {
    if (!wsReady) return;

    socketRef.current.send(JSON.stringify({ type: 'start_recording' }));

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);

    await audioContext.audioWorklet.addModule('pcm-processor.js');

    const pcmNode = new AudioWorkletNode(audioContext, 'pcm-processor');
    pcmNode.port.onmessage = (event) => {
      const float32Array = event.data;
      const trakingSpeak = float32Array.some(sample => Math.abs(sample) > 0.02);
      setIsSpeaking(trakingSpeak);

      // Resample the sample rate to 24000.
      let resampled;
      if (audioContext.sampleRate !== 24000) {
        resampled = resampleBuffer(float32Array, audioContext.sampleRate, 24000);
      } else {
        resampled = float32Array;
      }

      const pcm16 = convertToPCM16(resampled);
      const buffer = new Uint8Array(pcm16.buffer);
      let binary = '';
      for (let i = 0; i < buffer.byteLength; i++) {
        binary += String.fromCharCode(buffer[i]);
      }
      const base64Audio = btoa(binary);

      socketRef.current.send(JSON.stringify({
        type: 'audio_data',
        audio: base64Audio,
      }));
    };

    source.connect(pcmNode).connect(audioContext.destination);
    setIsRecording(true);
  };

  // Utility to resample Float32Array from srcRate to dstRate (linear interpolation)
  function resampleBuffer(buffer, srcRate, dstRate) {
    if (srcRate === dstRate) return buffer;
    const ratio = srcRate / dstRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      const srcIndex = i * ratio;
      const lower = Math.floor(srcIndex);
      const upper = Math.min(Math.ceil(srcIndex), buffer.length - 1);
      const weight = srcIndex - lower;
      result[i] = buffer[lower] * (1 - weight) + buffer[upper] * weight;
    }
    return result;
  }

  // stop recording the user audio
  const stopRecording = () => {
    socketRef.current.send(JSON.stringify({ type: 'stop_recording' }));

    processorRef.current?.disconnect();
    audioContextRef.current?.close();
    streamRef.current?.getTracks().forEach(track => track.stop());

    setIsRecording(false);
    stopPlayback()
  };

  // convert audio data from float to 16-bit PCM
  const convertToPCM16 = (float32Array) => {
    const pcm16 = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return pcm16;
  };

  const handleSetVoiceMode = () => {
    setVoiceMode((preV) => !preV)
    setNlopMod(false);
    setUserInput({ role: 'user', content: '', imageFiles: [], imagePreviews: [] })
    if(isRecording) {
      stopRecording()
    }
  }

  return (
    <>
      {isOpen ? <motion.div
        key={isOpen}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Draggable handle="#card_header" nodeRef={draggableNodeRef} bounds='#container' position={position} onDrag={handleDrag}>
          <div
            className={`${classes.card_container} ${resize && classes.resize} ${isOpen && classes.card_container_open}`}
            ref={draggableNodeRef}
          >
            <div className={classes.card_header} id='card_header'>
              <div className={classes.avatar_title}>
                <img
                  className={classes.header_avatar}
                  src="/images/chatbot.png"
                  alt="Chatbot"
                />
                <h1 className={classes.title}>Veritas Pulse Assistant</h1>
              </div>
              <div>
                <Tooltip title="Settings">
                  <MoreHorizIcon className={classes.icon} onClick={handleClickSettingsMenu} />
                </Tooltip>
                <Tooltip title="Minimize">
                  <RemoveIcon className={classes.icon} onClick={handleToggle} />
                </Tooltip>
                {resize ? (
                  <CloseFullscreenIcon
                    className={classes.resize_icon}
                    onClick={handleReSize}
                  />
                ) : (
                  <OpenInFullIcon
                    className={classes.resize_icon}
                    onClick={handleReSize}
                  />
                )}
                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  open={openSettingsMenu}
                  onClose={handleCloseSettingsMenu}
                  className={classes.menu}
                  MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                >
                  {SettingComponent()}
                </Menu>
              </div>

            </div>
            <div className={`${classes.chat_container}`}>
              {voiceMode ? <motion.div key={voiceMode}
                initial={framerMotionAnimation.initial}
                animate={framerMotionAnimation.animate}
                transition={framerMotionAnimation.transition}
                className={classes.voice_page}
                >

                {(isRecording && !isListening && !loadingVoiceResponse) ? (
                  <motion.div key={isListening} initial={framerMotionAnimation.initial}
                    animate={framerMotionAnimation.animate}
                    transition={framerMotionAnimation.transition} className={classes.bubble_container}>
                    <div className={`${classes.bubble} ${(isSpeaking) && classes.bubble_active}`}>
                    </div>
                  </motion.div>
                ) : (isRecording && isListening) ? (
                    <motion.div
                      key={isListening}
                      initial={framerMotionAnimation.initial}
                      animate={framerMotionAnimation.animate}
                      transition={framerMotionAnimation.transition}
                      className={classes.listen_bubbles_container}
                    >
                      {Array(4)
                        .fill()
                        .map((_, index) => (
                          <div key={index} className={`${classes.listen_bubble} ${isSpeakingFromPlayback && classes.listen_bubble_active}`} />
                        ))}
                    </motion.div>
                ) : (loadingVoiceResponse) ? (
                      <motion.div key={isListening} initial={framerMotionAnimation.initial}
                        animate={framerMotionAnimation.animate}
                        transition={framerMotionAnimation.transition}>
                        <CloudLoadingAnimation />
                      </motion.div>
                ) : null}


                <button
                  className={`${classes.mic_button} ${isRecording && classes.mic_button_listening}`}
                >
                  {(isListening && play) ? <StopIcon
                        className={classes.stop_listening_icon}
                        onClick={stopPlayback}
                      /> : isRecording ?  <Mic onClick={stopRecording}/> : <MicOff onClick={startRecording}/>}
                </button>

                <p className={classes.voice_case}>
                  {
                    isRecording && !isListening && !loadingVoiceResponse
                      ? 'Listening'
                      : isRecording && isListening
                        ? 'Speaking'
                        : loadingVoiceResponse
                          ? 'Processing'
                          : ''
                  }
                </p>

              </motion.div> : <motion.div key={voiceMode}
                initial={framerMotionAnimation.initial}
                animate={framerMotionAnimation.animate}
                transition={framerMotionAnimation.transition}
                >
                {chatHistory.map((message, index) => (
                  <div key={index}>
                    <div
                      ref={index === chatHistory.length - 1 ? lastMessageRef : null}
                      className={`${chatHistory.length - 1 === index && classes.last_message}`}
                    >
                      <div className={`${classes.message_box} ${message.role === 'assistant'
                        ? classes.bot_message_box
                        : classes.user_message_box
                        }`}>
                        <img
                          src={
                            message.role === "user"
                              ? gender === "F"
                                ? "/images/patient_woman.svg"
                                : "/images/patient_man.svg"
                              : "/images/chatbot.png"
                          }
                          alt={message.role === "user" ? "User" : "Chatbot"}
                          className={`${classes.message_avatar} ${message.role === 'assistant'
                            ? classes.bot_message_avatar
                            : classes.user_message_avatar
                            }`}
                        />
                        {message.role === 'assistant' ? (
                          <div className={`${classes.message} ${classes.bot_message}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {message.content}
                            </ReactMarkdown>
                            {index === chatHistory.length - 1 && <p ref={tailRef} className={classes.bot_message_tail}>.</p>}
                          </div>
                        ) : (
                          <>
                            <p className={`${classes.message} ${classes.user_message}`}>
                              {message.content}
                            </p>
                          </>
                        )}
                      </div>
                      <div>
                        {message?.imagePreviews?.length > 0 && <div className={classes.user_image_box}>
                          {message.imagePreviews.map((image, index) => (
                            <span className={classes.user_image_wrapper}>
                              <img key={index} alt='user_image' src={image} onClick={() => handleClickOpenImageDialog(image)} className={classes.user_image} />
                            </span>
                          ))}
                        </div>}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
              }
              {loading && (
                <div className={classes.loading_message_box}>
                  <img
                    src="/images/chatbot.png"
                    alt="Chatbot"
                    className={classes.loading_message_avatar}
                  />
                  <TypingDots />
                </div>
              )}
              {lastMessageRole === 'assistant' && !voiceMode && (
                <ArrowDownwardIcon
                  className={`${classes.scroll_down_icon} ${hideScrollIcon && classes.scroll_down_icon_active}`}
                  sx={{ fontSize: fontSizes['3xl'], transition: transitions.fast }}
                  onClick={handleScroll}
                />
              )}
            </div>

            <div className={classes.bottom}>
              <div className={classes.input_box}>
                {userInput.imagePreviews.length > 0 && <div className={classes.input_images_box}>
                  {userInput.imagePreviews.map((image, index) => {
                    return <div key={index} className={classes.input_image_box}>
                      <img src={image} alt='input_image' className={classes.input_image}></img>
                      <ClearIcon className={classes.input_image_clear_icon} onClick={() => handleDeleteImage(index)}/>
                    </div>
                  })}
                </div>}
                <TextField
                  className={classes.input}
                  placeholder="Ask something."
                  value={userInput.content}
                  multiline
                  variant="outlined"
                  maxRows={6}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={voiceMode}
                />
                <div className={classes.input_bottom_section}>
                  <button
                    className={`${classes.input_bottom_section_button} ${nlpMod && classes.input_bottom_section_button_active}`}
                    onClick={handleChangeMod}>
                    NLP Mode
                  </button>

                  <button
                    className={`${classes.input_bottom_section_button} ${voiceMode && classes.input_bottom_section_button_active}`}
                    onClick={handleSetVoiceMode}>
                    Voice Mode
                  </button>

                  <input ref={imageInputRef} hidden type='file' accept='image/*' onChange={handleSelectImages} />
                  <button className={classes.image_icon} disabled={nlpMod} onClick={() => imageInputRef.current.click()}>
                    <ImageIcon />
                  </button>

                  {loading || isStreaming ? (
                    <StopCircleIcon
                      sx={{ fontSize: fontSizes['3xl'] }}
                      className={`${classes.stop_streaming_icon} ${loading ? classes.disabled : ''}`}
                      onClick={stopStreaming}
                    />
                  ) : (
                    <ArrowUpwardIcon
                      sx={{ fontSize: fontSizes['3xl'] }}
                      className={`${classes.submit} ${userInput.content && classes.submit_active}`}
                      onClick={sendMessage}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Draggable>
      </motion.div> : (
          <>
            {(userRole === 'doctor' || userRole === 'patient') && (
              <div
                className={`${classes.bot_avatar_box} ${!chatbotCase && classes.bot_avatar_box_hide
                  }`}
              >
                <img
                  className={classes.bot_avatar}
                  src="/images/chatbot.png"
                  alt="Chatbot"
                  onClick={handleToggle}
                />
              </div>
            )}
          </>
      )}
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={'lg'}
      >
        <DialogContent>
          <img alt='dialog_image' src={dialogImage} className={classes.dialog_image}/>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Chatbot;